// theme.js
import { createTheme, alpha } from '@mui/material/styles';

// Custom keyframes for theme animations
export const animations = {
  goldShimmer: 'goldShimmer 2s linear infinite',
  floatSlow: 'floatSlow 25s ease-in-out infinite',
  floatMedium: 'floatMedium 30s ease-in-out infinite',
  floatFast: 'floatFast 20s ease-in-out infinite',
  shimmerBackground: 'shimmerBackground 4s ease-in-out infinite',
};

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#D4AF37',
      light: '#FFD700',
      dark: '#B8960C',
      contrastText: '#0a0a2a',
    },
    secondary: {
      main: '#0a0a2a',
      light: '#1a1a4a',
      dark: '#060620',
      contrastText: '#D4AF37',
    },
    background: {
      default: '#0a0a2a',
      paper: '#0d0d35',
      elevated: '#141445',
    },
    text: {
      primary: '#FFFFFF',
      secondary: alpha('#FFFFFF', 0.7),
      disabled: alpha('#FFFFFF', 0.5),
    },
    gold: {
      main: '#D4AF37',
      light: '#FFD700',
      dark: '#B8960C',
    },
    blue: {
      main: '#0a0a2a',
      light: '#1a1a4a',
      dark: '#060620',
    },
  },
  typography: {
    fontFamily: '"Inter", "Helvetica Neue", "Arial", sans-serif',
    h1: {
      fontFamily: '"Playfair Display", "Times New Roman", serif',
      fontWeight: 800,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontFamily: '"Playfair Display", "Times New Roman", serif',
      fontWeight: 800,
      letterSpacing: '-0.02em',
    },
    h3: {
      fontFamily: '"Playfair Display", "Times New Roman", serif',
      fontWeight: 700,
      letterSpacing: '-0.01em',
    },
    h4: {
      fontWeight: 700,
      letterSpacing: '-0.01em',
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
    button: {
      fontWeight: 700,
      letterSpacing: '1px',
      textTransform: 'uppercase',
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0 2px 4px rgba(0,0,0,0.1)',
    '0 4px 8px rgba(0,0,0,0.12)',
    '0 8px 16px rgba(0,0,0,0.14)',
    '0 12px 24px rgba(0,0,0,0.16)',
    '0 16px 32px rgba(0,0,0,0.18)',
    '0 20px 40px rgba(0,0,0,0.2)',
    '0 24px 48px rgba(0,0,0,0.22)',
    '0 28px 56px rgba(0,0,0,0.24)',
    '0 32px 64px rgba(0,0,0,0.26)',
    '0 36px 72px rgba(0,0,0,0.28)',
    '0 40px 80px rgba(0,0,0,0.3)',
    '0 44px 88px rgba(0,0,0,0.32)',
    '0 48px 96px rgba(0,0,0,0.34)',
    '0 52px 104px rgba(0,0,0,0.36)',
    '0 56px 112px rgba(0,0,0,0.38)',
    '0 60px 120px rgba(0,0,0,0.4)',
    '0 64px 128px rgba(0,0,0,0.42)',
    '0 68px 136px rgba(0,0,0,0.44)',
    '0 72px 144px rgba(0,0,0,0.46)',
    '0 76px 152px rgba(0,0,0,0.48)',
    '0 80px 160px rgba(0,0,0,0.5)',
    '0 84px 168px rgba(0,0,0,0.52)',
    '0 88px 176px rgba(0,0,0,0.54)',
    '0 92px 184px rgba(0,0,0,0.56)',
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '@keyframes goldShimmer': {
          '0%': { backgroundPosition: '-200% 0' },
          '100%': { backgroundPosition: '200% 0' },
        },
        '@keyframes floatSlow': {
          '0%': { transform: 'translate(0px, 0px) rotate(0deg)' },
          '33%': { transform: 'translate(20px, -15px) rotate(2deg)' },
          '66%': { transform: 'translate(-10px, 10px) rotate(-1deg)' },
          '100%': { transform: 'translate(0px, 0px) rotate(0deg)' },
        },
        '@keyframes floatMedium': {
          '0%': { transform: 'translate(0px, 0px) rotate(0deg)' },
          '33%': { transform: 'translate(-15px, -10px) rotate(-2deg)' },
          '66%': { transform: 'translate(15px, 8px) rotate(1deg)' },
          '100%': { transform: 'translate(0px, 0px) rotate(0deg)' },
        },
        '@keyframes floatFast': {
          '0%': { transform: 'translate(0px, 0px) scale(1)' },
          '50%': { transform: 'translate(8px, -8px) scale(1.05)' },
          '100%': { transform: 'translate(0px, 0px) scale(1)' },
        },
        '@keyframes shimmerBackground': {
          '0%': { opacity: '0.3' },
          '50%': { opacity: '0.7' },
          '100%': { opacity: '0.3' },
        },
        '@keyframes fadeInUp': {
          'from': { opacity: '0', transform: 'translateY(40px)' },
          'to': { opacity: '1', transform: 'translateY(0)' },
        },
        '@keyframes fadeInLeft': {
          'from': { opacity: '0', transform: 'translateX(-40px)' },
          'to': { opacity: '1', transform: 'translateX(0)' },
        },
        '@keyframes fadeInRight': {
          'from': { opacity: '0', transform: 'translateX(40px)' },
          'to': { opacity: '1', transform: 'translateX(0)' },
        },
        '@keyframes scaleIn': {
          'from': { opacity: '0', transform: 'scale(0.9)' },
          'to': { opacity: '1', transform: 'scale(1)' },
        },
        body: {
          scrollBehavior: 'smooth',
          backgroundColor: '#0a0a2a',
        },
        '::selection': {
          backgroundColor: alpha('#D4AF37', 0.3),
          color: '#FFFFFF',
        },
        '::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '::-webkit-scrollbar-track': {
          backgroundColor: alpha('#0a0a2a', 0.5),
        },
        '::-webkit-scrollbar-thumb': {
          backgroundColor: '#D4AF37',
          borderRadius: '4px',
        },
        '::-webkit-scrollbar-thumb:hover': {
          backgroundColor: '#FFD700',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'uppercase',
          fontWeight: 700,
          letterSpacing: '1px',
          padding: '10px 24px',
          transition: 'all 0.3s ease',
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)',
          color: '#0a0a2a',
          '&:hover': {
            background: 'linear-gradient(135deg, #FFD700 0%, #D4AF37 100%)',
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(212,175,55,0.3)',
          },
        },
        outlinedPrimary: {
          borderColor: '#D4AF37',
          color: '#D4AF37',
          '&:hover': {
            borderColor: '#FFD700',
            backgroundColor: alpha('#D4AF37', 0.1),
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          '@media (min-width: 1200px)': {
            paddingLeft: '32px',
            paddingRight: '32px',
          },
        },
      },
    },
  },
});

export default theme;